import { loadDailyPickRollover, type ShopBootstrap } from '~/services/bootstrap'
import { hydrateGridCandidate } from '~/services/catalogue'
import {
  deriveDailyPickCandidates,
  type DailyPickSource,
} from '~/services/daily-picks'
import {
  createHydratedSlots,
  type CatalogueGrid,
  type HydrationGate,
} from './catalogue-grid'

// The Daily Picks grid retains the response size to distinguish an empty
// draw from one whose cards were all omitted. Pool exhaustion uses the
// separate remaining-unowned count (ADR 0034).
export interface DailyPicksGrid extends CatalogueGrid {
  responseSize: number
}

// The Daily Picks grid reuses the Classic slot machinery (GLOSSARY.md "Daily
// pick collection" — cards follow the Classic card's rules): fixed slots in
// display order, each filling in place as its own native lookups settle.
// Tree and land-skin picks hydrate through the one shared path — the
// candidate's content decides its title key and art source. The gate is the
// Shop hydration order's (GLOSSARY.md); a replacement grid passes none and
// hydrates at once.
export function createDailyPicksGrid(
  source: DailyPickSource,
  options?: { gate?: HydrationGate },
): DailyPicksGrid {
  const grid = createHydratedSlots(
    deriveDailyPickCandidates(source),
    (candidate) =>
      hydrateGridCandidate(candidate, 'shop_daily_pick_product_omitted'),
    options,
  )
  return {
    ...grid,
    responseSize:
      source.collections['shop-daily-pick']
        .item_type_product_collection_products.length,
  }
}

// Shop's mid-session daily-pick refetch, shared by the daily-pick rollover
// and the Daily Picks refresh (ADR 0028, ADR 0032): refetch the daily-pick
// collection — only it — hydrate a replacement grid off-screen with no gate,
// and resolve once every slot has settled, ready to swap in as one write.
// The new assortment derives against the bootstrap ownership snapshot —
// ownership is untouched by either refetch, and session unlocks overlay at
// render time, as everywhere.
export async function loadReplacementDailyPicksGrid(
  data: ShopBootstrap,
): Promise<DailyPicksGrid> {
  const { collection, itemTypes } = await loadDailyPickRollover(
    data.currentUser.id,
  )
  const grid = createDailyPicksGrid({
    collections: { ...data.collections, 'shop-daily-pick': collection },
    itemTypes,
    unlockedLandSkins: data.unlockedLandSkins,
    unlockedTrees: data.unlockedTrees,
  })
  await grid.settled
  return grid
}
